import { useEffect, useState } from "react";
import classNames from "classnames";
import Container from "react-bootstrap/Container";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { contains } from "utils/index";
import { collapsedSidebarSelect, hasBannerSelect } from "Layout/slice/selector";
import { setCollapsedSidebar } from "Layout/slice";
import { loggedInState } from "features/Auth/slice/selector";
import { updateIsLoggedIn } from "features/Auth/slice/thunk";
import { fetchUserFlags } from "Layout/slice/thunk";
import { PLANS_PATH } from "features/Plans/routes";
import { MAIN_ACCOUNT_PATH } from "features/Account/routes";
import { HOME_PATH, DASHBOARD_PATH } from "features/Dashboard/routes";
import { SURMOUNT_ACCOUNT_DETAILS_QUERY_KEY } from "features/Dashboard/hook/useSurmountAccountDetails";

import { ReferralModal } from "shared";
import { TransferFundsModal } from "shared/Modal/TransferFundsModal";
import { NavbarContainer } from "./Navbar.styles";
import { NavbarPublic } from "./variants/NavbarPublic";
import { NavbarDashboard } from "./variants/NavbarDashboard";
import { NavbarDefault } from "./variants/NavbarDefault";

const isDashboardPath = (pathname) =>
  pathname === HOME_PATH || pathname.startsWith(DASHBOARD_PATH);

const isNoSidebarPath = (pathname) =>
  contains(pathname, MAIN_ACCOUNT_PATH) || contains(pathname, PLANS_PATH);

export const Navbar = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isLoggedInState = useSelector(loggedInState);
  const collapsed = useSelector(collapsedSidebarSelect);
  const hasBanner = useSelector(hasBannerSelect);
  const location = useLocation();

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTransferFundsModal, setShowTransferFundsModal] = useState(false);

  useEffect(() => {
    dispatch(updateIsLoggedIn());
  }, [dispatch]);

  // close mobile sidebar when resizing to desktop
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 769px)");
    const onChange = (e) => {
      if (e.matches && collapsed) {
        dispatch(setCollapsedSidebar(false));
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [collapsed, dispatch]);

  const noSidebar = isNoSidebarPath(location.pathname);

  const handlePlaidSuccess = () => {
    dispatch(fetchUserFlags());
    dispatch(updateIsLoggedIn());
    queryClient.invalidateQueries([SURMOUNT_ACCOUNT_DETAILS_QUERY_KEY]);
  };

  const renderNavbarContent = () => {
    if (!isLoggedInState) {
      return <NavbarPublic />;
    }

    if (isDashboardPath(location.pathname)) {
      return (
        <NavbarDashboard
          onPlaidSuccess={handlePlaidSuccess}
          onTransferFunds={() => setShowTransferFundsModal(true)}
        />
      );
    }

    return <NavbarDefault showHamburger={!noSidebar} />;
  };

  return (
    <NavbarContainer
      variant="dark"
      fixed="top"
      hasbanner={hasBanner.toString()}
      className={classNames({ "no-sidebar": noSidebar })}
    >
      <Container fluid>{renderNavbarContent()}</Container>

      {showInviteModal && (
        <ReferralModal onHide={() => setShowInviteModal(false)} />
      )}
      {showTransferFundsModal && (
        <TransferFundsModal
          show={showTransferFundsModal}
          onHide={() => setShowTransferFundsModal(false)}
        />
      )}
    </NavbarContainer>
  );
};
